/**
 * Everything the app knows, in IndexedDB.
 *
 * This is a CACHE, not the storage. What counts stands on the server; this
 * copy is here so that the app starts without waiting for a network and keeps
 * working without one. `sync.js` carries both directions.
 *
 * It used to be the other way round: the texts were true files on the disk,
 * written through the File System Access API. That gave a real file one could
 * back up and send — but only in Chrome and Edge. On the iPad at his mother's,
 * Safari fell back to this database anyway, so the guarantee did not hold on
 * the device that needed it most. A text that is on the server is on every
 * device, which is what was actually wanted.
 */

import type { Doc, DocData, Word, Mistake, Font, RawMistake } from '../types.js'

const DB_NAME = 'kritzl'
const DB_VERSION = 6
/** The texts: {id, title, body, personal, created, changed, rev, deleted}. */
const DOCS = 'docs'
/** Settings that belong to no document (font, dictionary). */
const PREFS = 'prefs'
/** Corrected words, across all texts: see `recordCorrection`. */
const WORDS = 'words'
/** Self-added fonts: {id, name, family, data, added}. */
const FONTS = 'fonts'
/** Mistakes found in photographed pages, not yet practised: see `addMistakes`. */
const MISTAKES = 'mistakes'
/**
 * The transcript of a photographed page: {id, text, at}.
 *
 * The PICTURE is not kept — it went to the model and is gone, and storing
 * photographs of a child's exercise book is a thing one does not do lightly.
 * The transcript is what the app worked from, and keeping it is what lets a
 * page be looked at again and taken off the list with its mistakes.
 */
const PAGES = 'pages'

let dbPromise: Promise<IDBDatabase> | null = null

function db(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const d = req.result
      if (!d.objectStoreNames.contains(DOCS)) {
        const s = d.createObjectStore(DOCS, { keyPath: 'id' })
        // Sorted by the last change, without loading everything.
        s.createIndex('changed', 'changed')
      }
      if (!d.objectStoreNames.contains(PREFS)) d.createObjectStore(PREFS)
      if (!d.objectStoreNames.contains(FONTS)) {
        d.createObjectStore(FONTS, { keyPath: 'id' })
      }
      if (!d.objectStoreNames.contains(WORDS)) {
        // The key is the misspelled word in lower case — "Fare" and "fare"
        // are the same mistake.
        const w = d.createObjectStore(WORDS, { keyPath: 'key' })
        w.createIndex('last', 'last')
      }
      if (!d.objectStoreNames.contains(PAGES)) {
        const p = d.createObjectStore(PAGES, { keyPath: 'id' })
        p.createIndex('at', 'at')
      }
      if (!d.objectStoreNames.contains(MISTAKES)) {
        // One row per finding, not per word: the same misspelling in two
        // photos is two occasions to practise, and the exercise wants to
        // know which page it came from.
        const m = d.createObjectStore(MISTAKES, { keyPath: 'id' })
        m.createIndex('at', 'at')
        m.createIndex('kind', 'kind')
      }
    }
    req.onsuccess = () => {
      const d = req.result
      // Another tab opens a newer version later: this connection must give
      // way, otherwise it blocks that tab forever.
      d.onversionchange = () => { d.close(); dbPromise = null }
      resolve(d)
    }
    req.onerror = () => reject(req.error)
    // Another tab still holds the old version open. Without this branch the
    // promise would hang silently — the app would then look frozen, with no
    // error in the console.
    req.onblocked = () => {
      reject(new Error(
        'Die Datenbank ist noch in einem anderen Tab geöffnet. ' +
        'Bitte die anderen Kritzl-Fenster schließen.'
      ))
    }
  })
  dbPromise.catch(() => { dbPromise = null })
  return dbPromise
}

/**
 * A transaction as a promise — IndexedDB cannot do that by itself.
 *
 * Generic over what the request yields, so the type carries through every
 * call below instead of stopping at `any` on the first line of the store.
 */
function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | void
): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(store, mode)
        const req = fn(t.objectStore(store))
        t.oncomplete = () => resolve((req as IDBRequest<T> | undefined)?.result as T)
        t.onerror = () => reject(t.error)
        t.onabort = () => reject(t.error)
      })
  )
}

// --- Settings --------------------------------------------------------------

export const getPref = <T>(key: string): Promise<T | undefined> =>
  tx<T | undefined>(PREFS, 'readonly', (s) => s.get(key))
export const setPref = (key: string, value: unknown): Promise<IDBValidKey> =>
  tx(PREFS, 'readwrite', (s) => s.put(value, key))

// --- The texts ------------------------------------------------------------

/**
 * A text: {id, title, body, personal, created, changed, rev, deleted}.
 *
 * `rev` counts up on every save and settles which side is newer when the
 * server and the device disagree. Whole text against whole text — a child
 * writes on one device at a time, and merging two versions of a school essay
 * line by line would produce something neither device wrote.
 */

/** The texts, the last changed first. Deleted ones stay out. */
export async function recent(limit = 12): Promise<Doc[]> {
  const all = await tx<Doc[]>(DOCS, 'readonly', (s) => s.getAll())
  return (all ?? [])
    .filter((d) => !d.deleted)
    .sort((a, b) => b.changed - a.changed)
    .slice(0, limit)
}

/** Every text, deleted ones included — the sync needs those as well. */
export const allDocs = (): Promise<Doc[]> =>
  tx<Doc[]>(DOCS, 'readonly', (s) => s.getAll()).then((a) => a ?? [])

export const getDoc = (id: string): Promise<Doc | undefined> =>
  tx<Doc | undefined>(DOCS, 'readonly', (s) => s.get(id))
export const putDoc = (doc: Doc): Promise<IDBValidKey> =>
  tx(DOCS, 'readwrite', (s) => s.put(doc))

/**
 * Writes a text and counts the revision up.
 *
 * @param doc   The existing text, or null for a new one.
 * @param data  {title, text, personal} — what stands on the paper.
 */
export async function saveDoc(doc: Doc | null, data: DocData): Promise<Doc> {
  const now = Date.now()
  const saved: Doc = {
    id: doc?.id ?? newId(),
    title: data.title ?? '',
    body: data.text ?? '',
    personal: data.personal ?? [],
    created: doc?.created ?? now,
    changed: now,
    rev: (doc?.rev ?? 0) + 1,
    deleted: null,
    fixes: data.fixes ?? doc?.fixes,
  }
  await putDoc(saved)
  return saved
}

/**
 * Deletes a text.
 *
 * The row stays with a timestamp instead of going away: a removed row would
 * be synced straight back down from the server, and the text would return
 * from the dead at the next start.
 */
export async function deleteDoc(id: string): Promise<void> {
  const d = await getDoc(id)
  if (!d) return
  await putDoc({ ...d, deleted: Date.now(), changed: Date.now(), rev: (d.rev ?? 1) + 1, body: '' })

  // A photographed page and its text are two views of one thing, so throwing
  // the text away throws the page away too — otherwise it would keep standing
  // in the photo list, pointing at a text that is gone.
  //
  // Written here rather than by calling `removePage`: that one calls this,
  // and the two would go round in circles.
  const page = await tx<Page | undefined>(PAGES, 'readonly', (s) => s.get(id))
  if (page && !page.deleted) {
    await tx(PAGES, 'readwrite', (s) => s.put({ ...page, deleted: Date.now() }))
    const all = await tx<Mistake[]>(MISTAKES, 'readonly', (s) => s.getAll())
    const mine = (all ?? []).filter((m) => m.pageId === id)
    await tx(MISTAKES, 'readwrite', (s) => { for (const m of mine) s.delete(m.id) })
  }
}

/** The content of a text, in the shape the editor wants. */
export const readDoc = (doc: Doc | null | undefined): DocData => ({
  title: doc?.title ?? '',
  text: doc?.body ?? '',
  personal: doc?.personal ?? [],
})

const newId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

// --- Corrected words -------------------------------------------------------

/**
 * Records that a misspelled word was put right.
 *
 * ONLY the true correction is recorded: a word that was marked and afterwards
 * stands there correctly. Not every typing mistake in passing — otherwise the
 * same word would count several times while writing, and the list would be
 * ruled by noise instead of by what Elmar really finds hard.
 *
 * It also records HOW he managed it. That is the real value: a word that
 * needed the solution three times stands differently from one he got right by
 * himself on the second try.
 *
 * @param wrong   How it stood there ("fare").
 * @param right   How it stands there now ("fahre").
 * @param stage   How far the help went when he solved it:
 *                0 = by himself, 1 = place shown, 2 = rule hint, 3 = solution.
 *                null when he corrected it without the help card open.
 * @param rule    The LanguageTool rule that found it. Kept so that the list
 *                can group: eleven missing commas are ONE thing he is
 *                practising, not eleven.
 */
export async function recordCorrection(
  wrong: string,
  right: string,
  stage: number | null = null,
  rule = '',
  context = ''
): Promise<Word> {
  const key = wrong.toLowerCase()
  const now = Date.now()
  const d: Word = (await tx<Word | undefined>(WORDS, 'readonly', (s) => s.get(key))) ?? {
    key,
    wrong,
    right: '',
    rule: '',
    corrections: [],
    count: 0,
    solo: 0,
    first: now,
    last: now,
  }

  d.count += 1
  d.last = now
  d.right = right
  if (rule) d.rule = rule
  // The sentence seen last wins: it is the freshest evidence of how the word
  // stands in his text.
  if (context) d.context = context
  // Count the independent corrections separately: progress shows in them,
  // not in the plain count.
  if (stage === 0 || stage === null) d.solo = (d.solo ?? 0) + 1
  // Keep the last attempts individually, for the history. Nobody needs more
  // than ten, and the database must not grow along.
  d.corrections = [...(d.corrections ?? []), { at: now, right, stage, rule }].slice(-10)

  await tx(WORDS, 'readwrite', (s) => s.put(d))
  return d
}

/**
 * All recorded words, the one corrected last first.
 *
 * Records without a rule are thrown away: they come from a version that did
 * not keep the rule yet, and without it they cannot be put into any group.
 * They are of no use in a list that is about groups — and they disappear by
 * themselves as soon as the word turns up once more.
 */
export async function corrections(): Promise<Word[]> {
  const all = (await tx<Word[]>(WORDS, 'readonly', (s) => s.getAll())) ?? []
  const stale = all.filter((d) => !d.rule)
  if (stale.length) {
    await tx(WORDS, 'readwrite', (s) => { for (const d of stale) s.delete(d.key) })
  }
  return all.filter((d) => d.rule).sort((a, b) => b.last - a.last)
}

/** A word as the server sent it — for the sync, which does not count up. */
export const putWord = (w: Word): Promise<IDBValidKey> =>
  tx(WORDS, 'readwrite', (s) => s.put(w))

export const clearCorrections = (): Promise<void> =>
  tx(WORDS, 'readwrite', (s) => s.clear())
export const deleteCorrection = (key: string): Promise<void> =>
  tx(WORDS, 'readwrite', (s) => s.delete(key))

// --- Mistakes from photographed pages -------------------------------------

/**
 * Stores what the model found on a photographed page.
 *
 * These are not corrections — nothing has been put right yet. They are the
 * INPUT for the exercises: the words Elmar got wrong in school, waiting to be
 * found and fixed on the phone. Each finding keeps its page and the sentence
 * around it, so the exercise can show the word in its own context.
 *
 * Only findings the model was SURE about are kept. A word it could not read
 * is not a mistake of the child, and must never become an exercise.
 *
 * @param page   {id, text, at} — the transcribed page.
 * @param found  [{wrong, right, kind, sure}] as the model returned them.
 * @returns the number stored.
 */
export async function addMistakes(
  page: { id: string; text: string; at: number },
  found: RawMistake[]
): Promise<number> {
  // The page first, and regardless of whether anything was found: a photograph
  // with no mistakes in it still happened, and the list should say so rather
  // than leave a gap.
  await tx(PAGES, 'readwrite', (s) => s.put(page)).catch(() => {})

  const rows: Mistake[] = found
    .filter((m) => m.sure !== false)
    .map((m, i) => ({
      id: `${page.id}-${i}`,
      pageId: page.id,
      wrong: m.wrong,
      right: m.right,
      kind: m.kind,
      // The sentence the model handed over, when it did. It reads the text
      // and knows which sentence it meant; `sentenceAround` has to guess
      // from the word, and guesses wrong when the word stands twice.
      context: m.sentence?.trim() || sentenceAround(page.text, m.wrong),
      at: page.at,
      // Filled in by the exercise: when it was solved, and how.
      solved: null,
    }))
  if (!rows.length) return 0
  await tx(MISTAKES, 'readwrite', (s) => { for (const r of rows) s.put(r) })
  return rows.length
}

/** All stored findings, the newest first. */
export async function mistakes(): Promise<Mistake[]> {
  const all = await tx<Mistake[]>(MISTAKES, 'readonly', (s) => s.getAll())
  return (all ?? []).sort((a, b) => b.at - a.at)
}

/** A finding as the server sent it — for the sync. */
export const putMistake = (m: Mistake): Promise<IDBValidKey> =>
  tx(MISTAKES, 'readwrite', (s) => s.put(m))

export const deleteMistake = (id: string): Promise<void> =>
  tx(MISTAKES, 'readwrite', (s) => s.delete(id))
export const clearMistakes = (): Promise<void> =>
  tx(MISTAKES, 'readwrite', (s) => s.clear())

/**
 * The sentence in which a word stands — for the exercise, which shows the
 * word in context rather than alone. First occurrence; a word that is wrong
 * twice on one page is stored twice anyway.
 */
function sentenceAround(text: string, word: string): string {
  const i = text.indexOf(word)
  if (i < 0) return ''
  const a = Math.max(text.lastIndexOf('.', i), text.lastIndexOf('\n', i)) + 1
  let z = text.length
  for (const c of ['.', '\n']) {
    const j = text.indexOf(c, i + word.length)
    if (j >= 0 && j < z) z = j + (c === '.' ? 1 : 0)
  }
  return text.slice(a, z).trim()
}

// --- Own fonts -------------------------------------------------------------

/**
 * Stores a font file.
 *
 * The raw `ArrayBuffer` is put down — IndexedDB handles binary data directly.
 * A base64 version in localStorage would be a third larger and would jump
 * over the 5 MB limit with the first font.
 *
 * The font stays in the browser: it is uploaded nowhere and sent nowhere.
 * "Upload" here means expressly only "put into the app".
 *
 * @param file  The file from the drag and drop or from the file dialog.
 */
export async function addFont(file: File): Promise<Font> {
  const data = await file.arrayBuffer()

  // Do not put down the same file twice: whoever drags a font in again means
  // the same one — and two identical entries in the list would only confuse.
  // Name and size are enough as an identifier; two different fonts with the
  // same name AND the same byte count practically do not exist.
  const existing = (await fonts()).find(
    (f) => f.name === file.name && f.size === data.byteLength
  )
  if (existing) return existing

  const doc: Font = {
    id: newId(),
    name: file.name,
    // An own family name per font — otherwise the second font added would
    // overwrite the first in the font register of the browser.
    family: `Kritzl Eigene ${Date.now().toString(36)}`,
    type: file.type || guessType(file.name),
    size: data.byteLength,
    data,
    added: Date.now(),
  }
  await tx(FONTS, 'readwrite', (s) => s.put(doc))
  return doc
}

/** All own fonts, the one added last first. */
export async function fonts(): Promise<Font[]> {
  const all = await tx<Font[]>(FONTS, 'readonly', (s) => s.getAll())
  return (all ?? []).sort((a, b) => b.added - a.added)
}

export const getFont = (id: string): Promise<Font | undefined> =>
  tx<Font | undefined>(FONTS, 'readonly', (s) => s.get(id))
export const deleteFont = (id: string): Promise<void> =>
  tx(FONTS, 'readwrite', (s) => s.delete(id))

/** Infer the type from the extension when the system says nothing. */
function guessType(name: string): string {
  const ext = name.toLowerCase().split('.').pop() ?? ''
  const types: Record<string, string> = {
    woff2: 'font/woff2',
    woff: 'font/woff',
    ttf: 'font/ttf',
    otf: 'font/otf',
  }
  return types[ext] ?? ''
}

/** File extensions that pass as a font. */
export const FONT_EXT = ['.woff2', '.woff', '.ttf', '.otf']

export const isFontFile = (name: string): boolean =>
  FONT_EXT.some((e) => name.toLowerCase().endsWith(e))

// --- Photographed pages -----------------------------------------------------

/**
 * Makes a text out of a photographed page.
 *
 * A transcript IS a text — it just came in through the camera rather than the
 * keyboard. Storing it as one means everything the app can do with a text it
 * can do with a photographed page too: open it, correct the mistakes in it,
 * have it checked again, print it in his own handwriting.
 *
 * The id is the page's, so the findings that came off it keep pointing at
 * something: they carry `pageId`, and that is now the id of a text.
 */
export async function docFromPage(page: Page): Promise<Doc> {
  const now = Date.now()
  const doc: Doc = {
    id: page.id,
    title: titleOf(page.text),
    body: page.text,
    personal: [],
    created: page.at,
    changed: page.at,
    rev: 1,
    deleted: null,
  }
  await putDoc(doc)
  return doc
}

/**
 * The first non-empty line, shortened — the same rule the editor uses.
 *
 * A photographed page has no title of its own; a child writes one at the top
 * or does not, and either way the first line is what he would call it.
 */
function titleOf(text: string): string {
  const line = text.split('\n').find((l) => l.trim()) ?? ''
  const t = line.trim()
  if (!t) return 'Abfotografierte Seite'
  if (t.length <= 60) return t
  const cut = t.slice(0, 60)
  const gap = cut.lastIndexOf(' ')
  return (gap > 20 ? cut.slice(0, gap) : cut) + '…'
}

/** The transcript of one photographed page. */
export interface Page {
  id: string
  text: string
  at: number
  /** Deleted means a timestamp, not gone — see `removePage`. */
  deleted?: number | null
}

/** The pages, newest first. Deleted ones stay out. */
export async function pages(): Promise<Page[]> {
  const all = await tx<Page[]>(PAGES, 'readonly', (s) => s.getAll())
  return (all ?? []).filter((p) => !p.deleted).sort((a, b) => b.at - a.at)
}

/** Every page, deleted ones included — the sync needs those as well. */
export async function allPages(): Promise<Page[]> {
  const all = await tx<Page[]>(PAGES, 'readonly', (s) => s.getAll())
  return all ?? []
}

/**
 * Empties everything that belongs to a person: texts, words, findings, pages.
 *
 * Settings and fonts stay — they belong to the device, not to whoever is
 * signed in. Called once, when a key turns out to open somebody else's
 * Kritzl; see `whoAmI` in `sync.ts`.
 */
export async function wipeContent(): Promise<void> {
  for (const name of [DOCS, WORDS, MISTAKES, PAGES]) {
    await tx(name, 'readwrite', (s) => s.clear())
  }
}

/** Takes down a page the sync brought. */
export async function putPage(p: Page): Promise<void> {
  await tx(PAGES, 'readwrite', (s) => s.put(p))
}

/**
 * Removes a page and everything that came off it.
 *
 * The mistakes go with it, and that is the point of being able to delete at
 * all: a page the model read badly puts wrong exercises into the rotation,
 * and taking the page away has to take them too.
 *
 * Already-practised mistakes go as well. They are evidence of that page, and
 * a half-removed page would leave the figures pointing at something that is
 * no longer there.
 */
export async function removePage(id: string): Promise<void> {
  const all = await tx<Mistake[]>(MISTAKES, 'readonly', (s) => s.getAll())
  const mine = (all ?? []).filter((m) => m.pageId === id)
  await tx(MISTAKES, 'readwrite', (s) => { for (const m of mine) s.delete(m.id) })

  // A timestamp, not a removal: the row has to stay so the sync can carry the
  // deletion to the other device. Removing it outright would have the page
  // come straight back at the next round.
  const page = await tx<Page | undefined>(PAGES, 'readonly', (s) => s.get(id))
  if (page) await tx(PAGES, 'readwrite', (s) => s.put({ ...page, deleted: Date.now() }))

  // And the text that came out of it. A transcript IS a text now, so the page
  // and the text are two views of one thing — leaving the text behind would
  // have the page gone from one list and still standing in the other.
  //
  // `deleteDoc` looks at the page as well and finds it already marked, so it
  // stops there: no circle.
  await deleteDoc(id).catch(() => {})
}

/** Takes mistakes out by id — for a page that was deleted on another device. */
export async function removeMistakes(ids: readonly string[]): Promise<void> {
  await tx(MISTAKES, 'readwrite', (s) => { for (const id of ids) s.delete(id) })
}

/** How many mistakes came off a page — for the line in the list. */
export async function mistakesOfPage(id: string): Promise<number> {
  const all = await tx<Mistake[]>(MISTAKES, 'readonly', (s) => s.getAll())
  return (all ?? []).filter((m) => m.pageId === id).length
}
